import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "./Logo";
import { scrollToSection } from "@/utils/scroll";

const LINKS = [
  { label: "Home", id: "top" },
  { label: "Plans", id: "plans" },
  { label: "Features", id: "features" },
  { label: "How It Works", id: "how" },
  { label: "FAQ", id: "faq" },
  { label: "Contact", id: "contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const go = (id) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate("/");
      setTimeout(() => scrollToSection("#" + id), 150);
    } else {
      scrollToSection("#" + id);
    }
  };

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/85 backdrop-blur-2xl border-b border-electric/15 shadow-[0_4px_30px_rgba(2,6,23,0.08)]" : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Logo />
        <div className="hidden items-center gap-8 lg:flex">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`navbar-${l.id}-link`}
              onClick={() => go(l.id)}
              className="text-sm font-medium text-slate-600 transition-colors duration-200 hover:text-electric"
            >
              {l.label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="navbar-view-plans-button"
            onClick={() => go("plans")}
            className="btn-primary hidden rounded-full px-6 py-2.5 text-sm sm:inline-flex"
          >
            View Plans
          </button>
          <button
            data-testid="navbar-hamburger-button"
            onClick={() => setOpen(!open)}
            aria-label="Toggle menu"
            className="inline-flex h-11 w-11 items-center justify-center rounded-xl border border-slate-900/10 bg-slate-900/[0.04] text-slate-700 lg:hidden"
          >
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="border-t border-slate-900/10 bg-white/95 backdrop-blur-2xl lg:hidden"
          >
            <div className="space-y-1 px-4 py-6">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`navbar-mobile-${l.id}-link`}
                  onClick={() => go(l.id)}
                  className="block w-full rounded-xl px-4 py-3 text-left font-display text-lg font-semibold text-slate-700 transition-colors hover:bg-slate-900/[0.04] hover:text-electric"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="navbar-mobile-view-plans-button"
                onClick={() => go("plans")}
                className="btn-primary mt-4 w-full rounded-2xl px-6 py-4 text-base"
              >
                View Plans
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
